import { Atom, Cmp, Mod, log, state } from ":mods";
import { SideTaskInfo } from "./components";
import { Match, Switch, createMemo, createSignal, splitProps, ErrorBoundary, createEffect, batch } from "solid-js";
import { SideTaskBar } from "./components/side-task-bar";
import { AMaterialTaskViewState, ASubmoduleMaterial, MaterialTaskRouterView } from "../../../models";
import clone from "clone";
import { Topic } from "./topics";
import { deleteCollectionDetails, getCollectionDetails, postTaskDetails, putTaskDetails } from "../../../apis";

export function MaterialTaskRouter(props: MaterialTaskRouterView) {
  const [local, others] = splitProps(props, ["$", "events", "elements", "options"]);
  const template = clone(local.elements.data);
  const $material_details = state.createAsync([
    getCollectionDetails,
    template.id,
    template.collection?.active_id,
    template, // initial_value
  ]);
  const [view_mode, set_view_mode] = createSignal<AMaterialTaskViewState>(local.options?.showState ?? "edit");
  // const $submission_status = state.create(getSubmissionStatus(cloned_details));
  const $material_details_backup = createMemo(() => {
    return $material_details.clone;
  });
  const design_name = local.elements.data.design_name;
  let update_payload = { formdata: undefined, data: undefined };
  let last_active_element = undefined;

  local.$.actions.updateLayout({
    sideInfo: [
      {
        title: "Task description:",
        element: () => <SideTaskInfo description={local.elements.data.description} tips={local.elements.data.tips} />,
      },
    ],
  });

  const Layer = local.$.actions.createLayerTemp({
    backMsg: "Back to topic",
    events: {
      backTo: undefined,
      afterBackTo: () => {
        local.events?.onBackFromRouter?.();
      },
    },
  });

  async function onSave(after?: "continue" | "show") {
    if (view_mode() !== "edit") {
      if (after === "continue") local.$.actions.layer.next();
      return;
    } else if (update_payload.data === undefined || update_payload.data === null) {
      local.$.actions.pushToast({
        type: "error",
        message: "you must update one field at least!",
      });
      return Promise.reject();
    }
    const data = $material_details.unwrap.value.data;
    const collectionNumber = data.collection?.active_id;
    const taskID = data.id;
    const has_submission = collectionNumber !== undefined && collectionNumber !== null && collectionNumber !== -1;
    try {
      let res = undefined;
      if (!has_submission) {
        res = await postTaskDetails(taskID, update_payload.formdata);
      } else {
        res = await putTaskDetails(taskID, collectionNumber, update_payload.formdata);
      }
      update_payload = { formdata: undefined, data: undefined };
      // console.log("res data :: ", res.data);
      $material_details.set(res);
      last_active_element = undefined;
      local.$.actions.pushToast({
        type: "success",
        message: "Topic updated successfully",
      });
      // after options
      if (after === "continue") local.$.actions.layer.next();
      else if (after === "show") set_view_mode("show");
      return Promise.resolve();
    } catch (res: any) {
      const err = res?.response?.data?.error[0];
      local.$.actions.pushToast({
        type: "error",
        message: err ?? "an error occured",
      });
    }
    return Promise.reject();
  }

  function onDeleteCollection(collectionID: string | number) {
    if (last_active_element !== undefined) {
      $material_details.set((s) => ({ ...s, data: last_active_element }));
      // $material_details.set((s) => ({ ...s, data: last_active_element }));
      last_active_element = undefined;
      return;
    }
    const details = $material_details.unwrap.value;
    const items = details.data.collection.items;
    if (items.length <= 1) {
      local.$.actions.pushToast({
        type: "error",
        message: "You can't delete the last collection item!",
      });
      return;
    }

    const task_id = $material_details.unwrap.value.data.id;
    deleteCollectionDetails(task_id, collectionID).then(() => {
      const idx = items.findIndex((item) => item.id === collectionID);
      if (idx < 0) {
        log.all.error(
          "FATAL: cannot find id in items array in material collection items :: idx :: ",
          idx,
          " :: id :: ",
          collectionID
        );
        return;
      }
      const new_items = clone(items);
      new_items.splice(idx, 1);
      const next_idx = idx === 0 ? 0 : idx >= new_items.length ? idx - 1 : idx;
      const active_id = new_items[next_idx]?.id;
      console.log("active id ", active_id, " :: ", details.data.id, " :: new items :: ", new_items, " :: ", items);
      $material_details.recall(details.data.id, active_id, template);
    });
  }

  function onDiscardChanges() {
    $material_details.set($material_details_backup());
    update_payload = {
      formdata: undefined,
      data: undefined,
    };
  }

  function onSwitchViewMode() {
    const vmode = view_mode();
    if (vmode === "show_only") {
      return;
    }
    set_view_mode((s) => (s === "show" ? "edit" : "show"));
  }
  return (
    <div class="flex flex-col w-full h-full">
      {/* wanna check if user in edit , then did a submission then jumped to show, then jumped back to edit, will he see the template or latest */}
      <Switch>
        <Match when={$material_details.state.loading}>
          <div class="flex w-full min-h-90vh items-center justify-center">
            <Atom.Loader.Platform
              title="Museum Taining Tasks"
              msg="Loading Task Form..."
              class="relative flex$col extend$ fight$"
            />
          </div>
        </Match>
        <Match when={$material_details.state.failed}>
          an error has occured while fetching collection {$material_details.error}
        </Match>
        <Match when={$material_details.state.success}>
          <Layer
            nonAnchorItems={[
              {
                element: () => (
                  <SideTaskBar
                    elements={{
                      data: clone($material_details.value.data),
                    }}
                    events={{
                      onAddNewCollection() {
                        // console.log("template ", template);
                        last_active_element = $material_details.unwrap.value.data;
                        batch(() => {
                          if (view_mode() !== "edit") {
                            set_view_mode("show");
                          }
                          $material_details.set((s) => {
                            const items = [...s.data.collection.items];
                            items.push({
                              id: -1,
                              title: "New Collection",
                            });
                            return {
                              ...s,
                              data: { ...template, collection: { ...template.collection, active_id: -1, items } },
                            };
                          });
                        });
                      },
                      onGetCollection(collectionID) {
                        let task_id = $material_details.unwrap.value.data.id;
                        if (design_name === "tp22_task01") {
                          // @ts-ignore
                          task_id = collectionID;
                          collectionID = -1;
                          // console.log("collection :: ", task_id);
                        }
                        $material_details.recall(task_id, collectionID, template);
                      },
                      onDeleteCollection(collectionID) {
                        // const task_id = $material_details.unwrap.value.data.id;
                        onDeleteCollection(collectionID);
                      },
                    }}
                  />
                ),
              },
            ]}
          >
            <ErrorBoundary fallback={<div>design name not found or unimplemented {design_name}</div>}>
              <Topic
                design_name={design_name}
                $={local.$}
                elements={{
                  data: $material_details.clone.data,
                }}
                events={{
                  onDataUpdate: (formdata, data) => {
                    // update_payload = merge(update_payload, { formdata, data });
                    update_payload = { formdata, data };
                    console.log("latest ", update_payload);
                  },
                }}
                options={{
                  editable: view_mode() === "edit" ? true : false,
                }}
              />

              <div class="flex flex-row w-full h-full gap-20px justify-end mt-40px mr-20px children:(!w-200px whitespace-nowrap px-3)">
                {view_mode() === "edit" && (
                  <>
                    <Atom.Form.Button
                      controls={{}}
                      statusValid="Discard changes"
                      class="form-btn bg!paper text!p border!p border-2 border-solid"
                      onClick={onDiscardChanges}
                    />
                    <Atom.Form.Button
                      controls={{}}
                      statusValid="Save"
                      statusInvalid="Save"
                      class="form-btn bg!paper text!p border!p border-2 border-solid"
                      onclick={() => onSave()}
                    />
                    <Atom.Form.Button
                      controls={{}}
                      statusValid="Save & Show"
                      class="form-btn"
                      onclick={() => onSave("show")}
                    />
                  </>
                )}
                <Atom.Form.Button
                  controls={{}}
                  statusValid={() => (view_mode() === "edit" ? "Save & Continue" : "Continue")}
                  class="form-btn"
                  onclick={() => onSave("continue")}
                />
                {view_mode() !== "show_only" && (
                  <Atom.Form.Button
                    controls={{}}
                    statusValid={() => (view_mode() === "edit" ? "Show" : "Edit")}
                    class="form-btn"
                    onclick={onSwitchViewMode}
                  />
                )}
              </div>
            </ErrorBoundary>
          </Layer>
        </Match>
      </Switch>
    </div>
  );
}
